import React, {
  ComponentType,
  FC,
  ReactElement,
  ReactHTMLElement,
  ReactNode,
} from 'react';
import styled, {
  css,
  FlattenSimpleInterpolation,
  StyledComponent,
} from 'styled-components';

type TTypographyParagraphVariant = 'subtitle' | 'text';
type TTypographyParagraph = {
  tag: 'p';
  variant: TTypographyParagraphVariant;
} & React.HTMLAttributes<HTMLParagraphElement>;
type TTypographyHeading = {
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'a';
} & React.HTMLAttributes<HTMLHeadElement>;
type TTypographyLink = {
  tag: 'a';
} & React.HTMLAttributes<HTMLLinkElement>;

export type TypographyProps = (
  | TTypographyHeading
  | TTypographyParagraph
  | TTypographyLink
) & {
  children: ReactNode;
};

const sharedCSS = css`
  display: flex;
  align-items: center;
  margin: unset;
  width: fit-content;
`;

const headingSharedCSS = css`
  font-family: 'Aeonik', sans-serif;
  font-weight: 700;
  color: var(--color-interface-content-normal);
`;

const paragraphSharedCSS = css`
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
`;

const StyledH1 = styled.h1`
  ${sharedCSS}
  ${headingSharedCSS};
  font-size: 50px;
  line-height: 52px;
`;
const StyledH2 = styled.h2`
  ${sharedCSS}
  ${headingSharedCSS};
  font-size: 32px;
  line-height: 32px;
`;
const StyledH3 = styled.h3`
  ${sharedCSS}
  ${headingSharedCSS};
  font-size: 24px;
  line-height: 24px;
`;
const StyledH4 = styled.h4`
  ${sharedCSS}
  ${headingSharedCSS};
  font-size: 19px;
  line-height: 20px;
`;
const StyledLink = styled.a`
  ${sharedCSS}
  ${paragraphSharedCSS};
  font-size: inherit;
  line-height: inherit;
  color: var(--color-interface-secondary-normal) !important;
  text-decoration: underline !important;
  display: inline;
`;

const StyledParagraph = styled.p<TTypographyParagraph>`
  ${sharedCSS}
  ${paragraphSharedCSS};
  ${(p) => paragraphVariantCssVars[p.variant as TTypographyParagraphVariant]}

  font-size: var(--l-font-size);
  line-height: var(--l-line-height);
`;

const paragraphVariantSubtitleCSSVars = css`
  --l-font-size: 18px;
  --l-line-height: 24px;
`;

const paragraphVariantTextCSSVars = css`
  --l-font-size: 14px;
  --l-line-height: 16px;
`;

const paragraphVariantCssVars: Record<
  TTypographyParagraphVariant,
  FlattenSimpleInterpolation
> = {
  subtitle: paragraphVariantSubtitleCSSVars,
  text: paragraphVariantTextCSSVars,
};

const typographyTagMapper = {
  h1: StyledH1,
  h2: StyledH2,
  h3: StyledH3,
  h4: StyledH4,
  a: StyledLink,
  p: StyledParagraph,
};

export const Typography: FC<TypographyProps> = ({
  tag,
  children,
  ...props
}) => {
  const Tag = typographyTagMapper[tag] as StyledComponent<
    ComponentType,
    ReactHTMLElement<
      HTMLHeadingElement | HTMLParagraphElement | HTMLLinkElement
    >
  >;

  return <Tag {...props}>{children}</Tag>;
};
